import axiosInstance from "../common/axiosInstance";
import { UseQueryResult, useQuery, UseMutationResult, useMutation, keepPreviousData } from "@tanstack/react-query";
import {
  AccountLevelThemesResponse,
  GetAccountLevelThemesParams,
} from "../../interfaces/themes";
import {
  FlightRiskApiResponse,
  GetFlightRiskParams,
  UpdateFlightRiskFieldsPayload,
} from "../../interfaces/flightRisk";
import { RelationshipTypes } from "../../utils/types";

// Define the endpoint path
export const PATHS = Object.freeze({
  GET_CLIENT_SNAPSHOT: "/client/snapshot",
  GET_QUICK_SUMMARY : "/client/quickSummary",
  GET_MONTHLY_INSIGHTS : "/client/monthly-insight",
  GET_CLIENT_INTERACTIONS: "/interactions/client-interactions",
  GET_CLIENT_STATS: "/client/week-stats",
  GET_RED_RATED_CLIENT: "/client/red-rated",
  GET_CLIENTS_MET: "/client/clients-met",
  GET_CLIENT_TALK_TIME_STATS: "/client/talktime-stats",
  GET_INTERACTION_QUALITY: "/client/interaction-quality",
  GET_ACTIVE_CLIENTS: "/client/active-clients",
  GET_FINGERTIP_METRICS: "/fingertip-metrics",
  GET_GENERATE_FINGERTIP_METRICS: "/generate-fingertip-metrics",
  GET_CLIENT_WEEK_STATS: "/client/week-stats/dynamic",
  GET_CLIENT_BUSINESS_STATS: "/client/business-health",
  GET_CLIENT_QUICK_TOPICS: "/client/client-quick-topics",
  GET_CLIENT_PORTFOLIO_HEALTH_TREND: "/client/red-clients-graph",
  GET_ACCOUNT_LEVEL_THEMES: "/account-level-themes",
  GET_FLIGHT_RISK_FOR_CLIENT: "/client/get-flight-risk-for-the-client",
  UPDATE_FLIGHT_RISK_FIELDS: "/client/flight-risk/fields",
});

// Define the parameter interface
interface GetClientSnapshotParams {
  startTime: string;
  endTime: string;
  employeeEmail: string;
  isRated: boolean;
}

interface GetQuickSummaryParams {
  interactionIds: string[];  // List of interaction IDs
}
interface GetMonthlyInsightsParams {
  startTime: string;
  endTime: string;
  domain: string;
}
interface GetClientInteractionsParams {
  employeePorId: string;    // Optional for interactions
  sortBy: string;
  sortOrder: string;
  startTime: string;
  endTime: string;
  offset: number;
  limit: number;
  isRated?: boolean;
  isForOrg?: boolean;
  analystOrManagerPorId?: string;
  relationshipType?: string;
  orgId: string | undefined;
  subOrgId: string | undefined;
  impersonatingPorId: string | undefined;
  accessLevel: string | undefined;
}
interface GetClientStatsParams {
}

interface GetRedRatedClientParams {
  startTime?: string;
  endTime?: string;
  employeePorId?: string;
  isForUser?: boolean;
}
interface GetClientsMetParams {
  startTime: string;
  endTime: string;
  offset: number;
  limit: number;
  sortBy: string;
  sortOrder: string;
  employeePorId?: string;
}
interface GetClientTalkTimeStatsParams {
  startTime: string;
  endTime: string;
  managerStats?: boolean;
  sortBy: string;
  sortOrder: string;
  offset: number;
  limit: number;
  employeePorId?: string;
  timeZone?: string;
  isSpaceMode?: boolean;
  spaceIds?: string[];
}

interface GetInteractionQualityParams {
  startTime: string;
  endTime: string;
  offset: number;
  limit: number;
  sortBy: string;
  sortOrder: string;
  filters: string[];
  employeePorId: string;
  timeZone?: string;
  isSpaceMode?: boolean;
  spaceIds?: string[];
}

interface GetClientBusinessStatsParams {
  startTime: string;
  endTime: string;
  classificationType: string;
  sortOrder: string;
  offset: number;
  limit: number;
  sortBy: string;
  employeePorId?: string;
  timeZone?: string;
  isSpaceMode?: boolean;
  spaceIds?: string[];
}
interface GetFingerTipmetricsParams {
  orgId: string | undefined;
  subOrgId: string;
}
interface GetClientWeekStatsParams {
  week1StartTime: string;
  week2StartTime: string;
  week3StartTime: string;
  week4StartTime: string;
  week5StartTime: string;
  timeZone?: string;
  isSpaceMode?: boolean;
  spaceIds?: string[];
}
interface GetClientQuickTopicsParams {
  orgId: string | undefined;
  subOrgId: string;
}
// Function to call the endpoint
const getClientSnapshot = async (params: GetClientSnapshotParams): Promise<any> => {
  const response = await axiosInstance.post(
    `${PATHS.GET_CLIENT_SNAPSHOT}`,   // Base URL
    {
      startTime: params.startTime,
      endTime: params.endTime,
      employeeEmail: params.employeeEmail,
    },
    {
      params: {
        isRated: params.isRated,
      }
    }
  );

  return response.data;
};

// React Query hook
const useGetClientSnapshot = (params: GetClientSnapshotParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params);
  
  return useQuery<any>({
    queryKey: ["get_client_snapshot", stringifiedParams],
    queryFn: () => getClientSnapshot(params),
    enabled: enabled,
    retry: 0,
  });
};

const getQuickSummary = async (params: GetQuickSummaryParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_QUICK_SUMMARY, {
    params: {
      interactionIds: params.interactionIds.join(","),  // Pass as comma-separated string
    }
  });

  return response.data;
};

const useGetQuickSummary = (params: GetQuickSummaryParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_quick_summary", stringifiedParams],
    queryFn: () => getQuickSummary(params),
    enabled: enabled,
    retry: 0,
  });
};

const getMonthlyInsights = async (params: GetMonthlyInsightsParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_MONTHLY_INSIGHTS, {
    params: {
      startTime: params.startTime,
      endTime: params.endTime,
      domain: params.domain,
    }
  });
  return response.data;
};

// React Query hook for `/client/monthly-insight`
const useGetMonthlyInsights = (params: GetMonthlyInsightsParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_monthly_insights", stringifiedParams],
    queryFn: () => getMonthlyInsights(params),
    enabled: enabled,
    retry: 0,
  });
};

const getClientInteractions = async (params: GetClientInteractionsParams): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_CLIENT_INTERACTIONS,
    {
      employeePorId: params.employeePorId,    // Optional
      startTime: params.startTime,
      endTime: params.endTime,
      isRated: params.isRated,
      orgId: params.orgId,
      subOrgId: params.subOrgId,
      ...(params.analystOrManagerPorId && {analystOrManagerPorId: params.analystOrManagerPorId}),
      ...(params.relationshipType && {relationshipType: params.relationshipType}),
    },
    {
      params: {
        limit: params.limit,
        offset: params.offset,
        sortOrder: params.sortOrder,
        sortBy: params.sortBy,
        ...(params.isForOrg && {isForOrg: params.isForOrg}),
        ...(params.impersonatingPorId && { impersonatingPorId: params.impersonatingPorId }),
        ...(params.accessLevel && { accessLevel: params.accessLevel }),
      }
    }
  );

  return response.data;
};

// React Query hook for `/client-interactions`
const useGetClientInteractions = (params: GetClientInteractionsParams, enabled: boolean) => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_client_interactions", stringifiedParams],
    queryFn: () => getClientInteractions(params),
    enabled: enabled,
    retry: 0,
  });
};

const getClientStats = async (): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_CLIENT_STATS, {
  });
  return response.data;
};

const useGetClientStats = (params?: GetClientStatsParams, enabled: boolean = true) => {
  const stringifiedParams = JSON.stringify(params || {});
  return useQuery<any>({
    queryKey: ["get_client_stats", stringifiedParams],
    queryFn: () => getClientStats(),
    enabled,
    retry: 0,
  });
};

const getRedRatedClient = async (params?: GetRedRatedClientParams): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_RED_RATED_CLIENT, {
    params: {
      ...(params?.startTime && { startTime: params.startTime }),
      ...(params?.endTime && { endTime: params.endTime }),
      ...(params?.employeePorId && { employeePorId: params.employeePorId }),
      ...(params?.isForUser && { isForUser: params.isForUser }),
    },
  });
  return response.data;
};

const useGetRedRatedClient = (params?: GetRedRatedClientParams, enabled: boolean = true) => {
  const stringifiedParams = JSON.stringify(params || {});
  return useQuery<any>({
    queryKey: ["get_red_rated_client", stringifiedParams],
    queryFn: () => getRedRatedClient(params),
    enabled,
    retry: 0,
  });
};

const getInteractionQuality = async(params: GetInteractionQualityParams) : Promise<any> =>{
  const response = await axiosInstance.post(PATHS.GET_INTERACTION_QUALITY,
    {
      startTime: params.startTime,
      endTime: params.endTime,
      timeZone: params.timeZone,
      ...(params.filters?.length && { filters: params.filters }),
      employeePorId: params.employeePorId,
      ...(params.isSpaceMode && { isSpaceMode: params.isSpaceMode }),
      ...(params.spaceIds && { spaceIds: params.spaceIds }),
    },
    {
    params: {
      limit: params.limit,
      offset: params.offset,
      sortOrder: params.sortOrder,
      sortBy: params.sortBy,
    }
  }
  );
  return response.data;
};

const useGetInteractionQuality = (params: GetInteractionQualityParams, enabled: boolean = true) => {
  const stringifiedParams = JSON.stringify(params || {});
  return useQuery<any>({
    queryKey: ["get_interaction_quality", stringifiedParams],
    queryFn: () => getInteractionQuality(params),
    enabled,
    retry: 0,
  });
}

const getClientsMet = async (params: GetClientsMetParams): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_CLIENTS_MET,
    {},
    {
      params: {
        startTime: params.startTime,
        endTime: params.endTime,
        offset: params.offset,
        limit: params.limit,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
        ...(params.employeePorId && { employeePorId: params.employeePorId }),
      },
    }
  );

  return response.data;
};

const useGetClientsMet = (params: GetClientsMetParams, enabled: boolean = true) => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_clients_met", stringifiedParams],
    queryFn: () => getClientsMet(params),
    enabled,
    retry: 0,
  });
};

const getClientTalkTimeStats = async (
  params: GetClientTalkTimeStatsParams
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_CLIENT_TALK_TIME_STATS,
    {
      startTime: params.startTime,
      endTime: params.endTime,
      timeZone: params.timeZone,
      ...(params.managerStats !== undefined && { managerStats: params.managerStats }),
      ...(params.employeePorId && { employeePorId: params.employeePorId }),
      ...(params.isSpaceMode && { isSpaceMode: params.isSpaceMode }),
      ...(params.spaceIds && { spaceIds: params.spaceIds }),
    },
    {
      params: {
        limit: params.limit,
        offset: params.offset,
        sortBy: params.sortBy,
        sortOrder: params.sortOrder,
      },
    }
  );

  return response.data;
};

const useGetClientTalkTimeStats = (
  params: GetClientTalkTimeStatsParams,
  enabled: boolean = true
) => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_client_talktime_stats", stringifiedParams],
    queryFn: () => getClientTalkTimeStats(params),
    enabled,
    retry: 0,
  });
};

const getActiveClients = async (isSpaceMode?: boolean, spaceIds?: string[], orgName?: string, isHierarchy?: boolean, relationshipType?: RelationshipTypes[]): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_ACTIVE_CLIENTS, {
    params: {
      ...(isSpaceMode ? { isSpaceMode: true } : {}),
      ...(spaceIds && spaceIds.length > 0 && { spaceIds: spaceIds.join(",") }),
      ...(orgName?.trim() ? { orgName: orgName.trim() } : {}),
      ...(isHierarchy ? { isHierarchy: true } : {}),
      ...(relationshipType &&
        (Array.isArray(relationshipType)
          ? relationshipType.length > 0 && { relationshipType: relationshipType.join(",") }
          : { relationshipType })),
    },
  });
  return response;
};

const useGetActiveClients = (enabled: boolean, isSpaceMode?: boolean, spaceIds?: string[], orgName?: string, isHierarchy?: boolean, relationshipType?: RelationshipTypes[]) => {
  return useQuery<any>({
    queryKey: [
      "get_active_clients",
      Boolean(isSpaceMode),
      JSON.stringify(spaceIds || []),
      orgName?.trim() || "",
      Boolean(isHierarchy),
      Array.isArray(relationshipType)
        ? JSON.stringify(relationshipType)
        : relationshipType || "",
    ],
    queryFn: () => getActiveClients(isSpaceMode, spaceIds, orgName, isHierarchy, relationshipType),
    enabled,
    placeholderData: keepPreviousData,
    retry: 0,
  });
};


const getFingertipMetrics = async(params: GetFingerTipmetricsParams):Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_FINGERTIP_METRICS,{
    params: {
      orgId: params.orgId,
      subOrgId: params.subOrgId
    }
  });
  return response;
}

const useGetFingerTipMetrics = (params:GetFingerTipmetricsParams, enabled: boolean ) => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_fingertip_metrics", stringifiedParams],
    queryFn: () => getFingertipMetrics(params),
    enabled,
    retry: 0,
  });
}

const getGenerateFingertipMetrics = async (params: GetFingerTipmetricsParams):Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_GENERATE_FINGERTIP_METRICS,{
    params: {
      orgId: params.orgId,
      subOrgId: params.subOrgId
    }
  });
  return response;
}

const useGetGenerateFingertipMetrics = (params:GetFingerTipmetricsParams, enabled: boolean ) => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_generate_fingertip_metrics", stringifiedParams],
    queryFn: () => getGenerateFingertipMetrics(params),
    enabled,
    retry: 0,
  });
}

const getClientWeekStats = async (params?: GetClientWeekStatsParams): Promise<any> => {
  const response = await axiosInstance.post(PATHS.GET_CLIENT_WEEK_STATS, {
    week1StartTime: params?.week1StartTime,
    week2StartTime: params?.week2StartTime,
    week3StartTime: params?.week3StartTime,
    week4StartTime: params?.week4StartTime,
    week5StartTime: params?.week5StartTime,
    timeZone: params?.timeZone,
    isSpaceMode: params?.isSpaceMode,
    spaceIds: params?.spaceIds,
  });
  return response.data;
};

const useGetClientWeekStats = (params?: GetClientWeekStatsParams, enabled: boolean = true) => {
  const stringifiedParams = JSON.stringify(params || {});
  return useQuery<any>({
    queryKey: ["get_client_week_stats", stringifiedParams],
    queryFn: () => getClientWeekStats(params),
    enabled,
    retry: 0,
  })
};
const getClientBusinessStats = async (
  params: GetClientBusinessStatsParams
): Promise<any> => {

  const response = await axiosInstance.post(
    PATHS.GET_CLIENT_BUSINESS_STATS,
    {
      startTime: params.startTime,
      endTime: params.endTime,
      classificationType: params.classificationType,
      employeePorId: params.employeePorId,
      timeZone: params.timeZone,
      isSpaceMode: params.isSpaceMode,
      spaceIds: params.spaceIds,
    },
    {
      params: {
        limit: params.limit,
        offset: params.offset,
        sortOrder: params.sortOrder,
        sortBy: params.sortBy,
      },
    }
  );

  return response;
};
const useGetClientBusinessStats = (
  params: GetClientBusinessStatsParams,
  enabled: boolean = true
) => {

  const stringifiedParams = JSON.stringify(params);

  return useQuery<any>({
    queryKey: ["get_client_business_stats", stringifiedParams],
    queryFn: () => getClientBusinessStats(params),
    enabled,
    retry: 0,
  });
};

const getClientQuickTopics = async(
  params: GetClientQuickTopicsParams
): Promise<any> => {
  const response = await axiosInstance.get(PATHS.GET_CLIENT_QUICK_TOPICS, 
    {
      params: {
        orgId: params.orgId,
        subOrgId: params.subOrgId
      },
    }
  )
  return response;
};

const useGetClientQuickTopics = (
  params: GetClientQuickTopicsParams,
  enabled: boolean
) => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<any>({
    queryKey: ["get_client_quick_topics", stringifiedParams],
    queryFn: () => getClientQuickTopics(params),
    enabled,
    retry: 0,
  });
}

const getClientPortfolioHealthTrend = async ({
  isSpaceMode,
  spaceIds,
}: {
  isSpaceMode?: boolean;
  spaceIds?: string[];
}): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.GET_CLIENT_PORTFOLIO_HEALTH_TREND,
    {
      isSpaceMode,
      spaceIds,
    }
  );
  return response;
};

const useGetClientPortfolioHealthTrend = (
  enabled: boolean,
  isSpaceMode?: boolean,
  spaceIds?: string[],
) => {
  return useQuery<any>({
    queryKey: ["get_client_portfolio_health_trend", isSpaceMode, spaceIds,],
    queryFn: () =>
      getClientPortfolioHealthTrend({
        isSpaceMode,
        spaceIds,
      }),
    enabled,
    retry: 0,
  });
};

const getAccountLevelThemes = async (
  params: GetAccountLevelThemesParams,
): Promise<AccountLevelThemesResponse> => {
  const response = await axiosInstance.post(PATHS.GET_ACCOUNT_LEVEL_THEMES, {
    startTime: params.startTime,
    endTime: params.endTime,
    classification: params.classification,
    subOrgId: params.subOrgId,
  });

  return response.data;
};

const useGetAccountLevelThemes = (
  params: GetAccountLevelThemesParams,
  enabled: boolean = true,
) => {
  const stringifiedParams = JSON.stringify(params);

  return useQuery<AccountLevelThemesResponse>({
    queryKey: ["get_account_level_themes", stringifiedParams],
    queryFn: () => getAccountLevelThemes(params),
    enabled,
    retry: 0,
  });
};

const getFlightRiskForClient = async (
  params: GetFlightRiskParams
): Promise<FlightRiskApiResponse> => {
  const response = await axiosInstance.get(PATHS.GET_FLIGHT_RISK_FOR_CLIENT, {
    params: {
      orgId: params.orgId,
      subOrgId: params.subOrgId,
    },
  });
  return response.data;
};

const useGetFlightRiskForClient = (
  params: GetFlightRiskParams,
  enabled: boolean
) => {
  const stringifiedParams = JSON.stringify(params);
  return useQuery<FlightRiskApiResponse>({
    queryKey: ["get_flight_risk_for_client", stringifiedParams],
    queryFn: () => getFlightRiskForClient(params),
    enabled,
    retry: 0,
  });
};

const updateFlightRiskFields = async (
  payload: UpdateFlightRiskFieldsPayload
): Promise<any> => {
  const response = await axiosInstance.post(
    PATHS.UPDATE_FLIGHT_RISK_FIELDS,
    payload
  );
  return response.data;
};

const useUpdateFlightRiskFields = (
  handlerFunctions?: any
): UseMutationResult<any, any, UpdateFlightRiskFieldsPayload> => {
  return useMutation({
    mutationKey: ["update_flight_risk_fields"],
    mutationFn: updateFlightRiskFields,
    ...handlerFunctions,
  });
};

// Export the service
export interface ClientService {
  readonly useGetClientSnapshot: (
    params: GetClientSnapshotParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetQuickSummary: (
    params: GetQuickSummaryParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;
  readonly useGetMonthlyInsights: (
    params: GetMonthlyInsightsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetClientInteractions: (
    params: GetClientInteractionsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetClientStats: (
    params?: GetClientStatsParams,
    enabled?: boolean
  ) => UseQueryResult<any, any>; 

  readonly useGetRedRatedClient: (
    Params?: GetRedRatedClientParams,
    enabled?: boolean,
  ) => UseQueryResult<any, any>;  

  readonly useGetClientsMet: (
    params: GetClientsMetParams,
    enabled?: boolean,
  ) => UseQueryResult<any, any>;  

  readonly useGetInteractionQuality: (
    params: GetInteractionQualityParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetClientTalkTimeStats: (
    params: GetClientTalkTimeStatsParams,
    enabled?: boolean,
  ) => UseQueryResult<any, any>;

  readonly useGetActiveClients: (
    enabled: boolean,
    isSpaceMode?: boolean,
    spaceIds?: string[],
    orgName?: string,
    isHierarchy?: boolean,
    relationshipType?: RelationshipTypes[],
  ) => UseQueryResult<any, any>;

  readonly useGetFingerTipMetrics: (
    params: GetFingerTipmetricsParams,
    enabled: boolean,
  ) => UseQueryResult<any, any>;

  readonly useGetGenerateFingertipMetrics: (
    params: GetFingerTipmetricsParams,
    enabled: boolean,
  ) => UseQueryResult<any, any>;
  
  readonly useGetClientWeekStats: (
    params: GetClientWeekStatsParams,
    enabled: boolean,
  ) => UseQueryResult<any, any>;
  readonly useGetClientBusinessStats: (
    params: GetClientBusinessStatsParams,
    enabled?: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetClientQuickTopics: (
    params: GetClientQuickTopicsParams,
    enabled: boolean
  ) => UseQueryResult<any, any>;

  readonly useGetClientPortfolioHealthTrend: (
    enabled: boolean,
    isSpaceMode?: boolean,
    spaceIds?: string[],
  ) => UseQueryResult<any, any>;

  readonly useGetAccountLevelThemes: (
    params: GetAccountLevelThemesParams,
    enabled?: boolean,
  ) => UseQueryResult<AccountLevelThemesResponse, any>;

  readonly useGetFlightRiskForClient: (
    params: GetFlightRiskParams,
    enabled: boolean
  ) => UseQueryResult<FlightRiskApiResponse, any>;

  readonly updateFlightRiskFields: (
    payload: UpdateFlightRiskFieldsPayload
  ) => Promise<any>;

  readonly useUpdateFlightRiskFields: (
    handlerFunctions?: any
  ) => UseMutationResult<any, any, UpdateFlightRiskFieldsPayload>;
}


const ClientService: ClientService = Object.freeze({
  useGetClientSnapshot,
  useGetQuickSummary,
  useGetMonthlyInsights,
  useGetClientInteractions,
  useGetClientStats,
  useGetRedRatedClient,
  useGetClientsMet,
  useGetClientTalkTimeStats,
  useGetInteractionQuality,
  useGetActiveClients,
  useGetFingerTipMetrics,
  useGetGenerateFingertipMetrics,
  useGetClientWeekStats,
  useGetClientBusinessStats,
  useGetClientQuickTopics,
  useGetClientPortfolioHealthTrend,
  useGetAccountLevelThemes,
  useGetFlightRiskForClient,
  updateFlightRiskFields,
  useUpdateFlightRiskFields,
});

export default ClientService;
